import { Checkbox, InventoryNumber, Price } from '@artosera/ui';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { OeuvrePresentee } from '../../api/oeuvres.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import styles from './CarteOeuvre.module.css';

export interface CarteOeuvreProps {
  oeuvre: OeuvrePresentee;
  vignetteUrl: string | null;
  langue: 'fr' | 'en';
  /** La fiche de cette œuvre est ouverte à droite. */
  active: boolean;
  /** `null` : le rôle ne peut rien sélectionner, pas de case. */
  cochee: boolean | null;
  prixArtisteVisible: boolean;
  onOuvrir: () => void;
  onBasculerCase: () => void;
}

/**
 * Une œuvre en carte : la vignette entière sur son passe-partout, l'artiste,
 * le titre en serif d'affichage, l'année et le numéro d'inventaire, puis le
 * statut et le prix. Le prix n'apparaît que s'il est rendu par l'API pour le
 * rôle courant (`null` sinon).
 */
export function CarteOeuvre({
  oeuvre,
  vignetteUrl,
  langue,
  active,
  cochee,
  prixArtisteVisible,
  onOuvrir,
  onBasculerCase,
}: CarteOeuvreProps): JSX.Element {
  const { t } = useTranslation(['oeuvres', 'regie']);
  return (
    <li className={styles.carte} data-selected={active} data-ui="inventory-card">
      <button
        type="button"
        className={styles.ouvrir}
        data-ui="inventory-card-open"
        data-active={active ? 'true' : undefined}
        onClick={onOuvrir}
        aria-label={t('regie:oeuvres.ouvrirFiche', { titre: oeuvre.titre })}
      >
        <VignetteOeuvre taille="lg" className={styles.vignette} url={vignetteUrl} />
        <span className={styles.corps}>
          <span className={styles.artiste}>{oeuvre.artisteNom}</span>
          <span className={styles.titre}>{oeuvre.titre}</span>
          <span className={styles.reperes}>
            {oeuvre.annee !== null ? <span className={styles.annee}>{oeuvre.annee}</span> : null}
            <InventoryNumber value={oeuvre.numeroInventaire} />
          </span>
        </span>
      </button>
      <div className={styles.pied}>
        <PastilleStatut statut={oeuvre.statut} />
        <span className={styles.prix}>
          {oeuvre.prixPublicCentimes !== null ? (
            <Price cents={oeuvre.prixPublicCentimes} lang={langue} currency={oeuvre.devise} />
          ) : null}
          {prixArtisteVisible && oeuvre.prixArtisteCentimes ? (
            <span className={styles.prixArtiste}>
              {t('oeuvres:champs.prixArtiste')}{' '}
              <Price cents={oeuvre.prixArtisteCentimes} lang={langue} currency={oeuvre.devise} />
            </span>
          ) : null}
        </span>
      </div>
      {cochee !== null ? (
        <div className={styles.case}>
          <Checkbox
            label=""
            aria-label={t('regie:oeuvres.listeDePrixSelection.selectionnerOeuvre', {
              titre: oeuvre.titre,
            })}
            checked={cochee}
            onCheckedChange={onBasculerCase}
          />
        </div>
      ) : null}
    </li>
  );
}
